import React, { useState, useEffect } from 'react';
import { useFinanceProfile } from '../../moduleServices/finance_profilecontext';
import { fetchMetadataCategoriesHierarchical } from '../../moduleServices/flow_requests';
import { toast } from 'react-hot-toast';
import SpinnerLoad from '../../moduleComponents/SpinnerLoad';
import DropdownCustom from '../../moduleComponents/DropdownCustom';
import DropdownCurrency from '../../moduleComponents/DropdownCurrency';
import TooltipCustom from '../../moduleComponents/TooltipCustom';
import { InformationCircleIcon } from '@heroicons/react/24/outline';

const CardFinanceProfile = () => {
  const { profile, updateProfile, currencyOptions, loading: loadingProfile } = useFinanceProfile();
  const [categories, setCategories] = useState([]);
  const [selected, setSelected] = useState('');
  const [selectedCurrency, setSelectedCurrency] = useState('BRL');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const fetchCategories = async () => {
      setLoading(true);
      try {
        const data = await fetchMetadataCategoriesHierarchical();
        
        // Extract unique L0 names (without type)
        const uniqueL0Names = [...new Set(
          Object.values(data.by_l0 || {}).map(obj => obj.name)
        )].sort();
        
        const l0Options = uniqueL0Names.map(name => ({
          value: name,
          label: name
        }));
        
        setCategories(l0Options);
      } catch (err) {
        setCategories([]);
      } finally {
        setLoading(false);
      }
    };
    fetchCategories();
  }, []);

  useEffect(() => {
    setSelected(profile?.default_categ_l0 || '');
    setSelectedCurrency(profile?.default_currency || 'BRL');
  }, [profile]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const valueToSave = typeof selected === 'object' ? selected.value : selected;
      await updateProfile({ 
        default_categ_l0: valueToSave,
        default_currency: selectedCurrency
      });
      toast.success('Finance profile updated!');
    } catch (err) {
      const errorMessage = err.message || 'Error updating finance profile';
      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white p-3 rounded-2xl shadow flex flex-col mb-2">
      <div className="flex justify-between items-center mb-2">
        <h2 className="text-lg font-semibold">Finance Profile</h2>
      </div>
      {loadingProfile || loading ? (
        <div className="flex justify-center py-4"><SpinnerLoad /></div>
      ) : (
        <div className="flex items-center gap-3 flex-wrap">
          <label className="text-xs text-gray-600 font-medium mr-1 whitespace-nowrap">Default Group</label>
          <DropdownCustom
            options={categories}
            selectedValue={selected}
            onSelect={setSelected}
            placeholder="(None / All)"
            width="w-36"
            showAllOption={true}
            allOption={{ value: '', label: '(None)' }}
          />
          
          <label className="text-xs text-gray-600 font-medium mr-1 ml-4 whitespace-nowrap">Default Currency</label>
          <DropdownCurrency
            selectedValue={selectedCurrency}
            onSelect={setSelectedCurrency}
            options={currencyOptions}
          />
          
          <button
            className="bg-gray-700 hover:bg-gray-800 text-white px-3 py-1 rounded-lg text-sm font-medium disabled:opacity-60 ml-2"
            onClick={handleSave}
            disabled={
              saving || 
              (
                (typeof selected === 'object' ? selected.value : selected) === (profile?.default_categ_l0 || '') &&
                selectedCurrency === (profile?.default_currency || 'BRL')
              )
            }
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
          <TooltipCustom 
            text="The default 'Group' and 'Currency' will be used as the initial filter in graphs, dashboards and modals."
            position="auto"
            width="w-56"
          >
            <InformationCircleIcon className="h-5 w-5 text-gray-400 cursor-pointer ml-1" />
          </TooltipCustom>
        </div>
      )}
    </div>
  );
};

export default CardFinanceProfile;
